import { createRouter, createWebHistory } from 'vue-router';
import MessageHub from '~/components/message-hub/MessageHub.vue';
import MessageHubBulkActions from '~/components/message-hub/MessageHubBulkActions.vue';
import MessageHubContactView from '~/components/message-hub/MessageHubContactView.vue';
import MessageHubConversation from '~/components/message-hub/MessageHubConversation.vue';
import MessageHubNewConversation from '~/components/message-hub/MessageHubNewConversation.vue';
import MessageHubScheduledMessages from '~/components/message-hub/message-hub-scheduled-messages/MessageHubScheduledMessages.vue';
import MessageHubScheduledMessage from '~/components/message-hub/message-hub-scheduled-messages/MessageHubScheduledMessage.vue';
import MessageHubNoThreadSelected from '~/components/message-hub/message-hub-threads/MessageHubNoThreadSelected.vue';
import { canUseMessageHub } from './components/message-hub/messageHubUtil';
import CampaignList from '~/components/campaigns/list/CampaignList.vue';
import CampaignStepForm from '~/components/campaigns/campaign-step-form/CampaignStepForm.vue';
import CampaignContacts from '~/components/campaigns/contacts/CampaignContacts.vue';
import CampaignDetails from '~/components/campaigns/CampaignDetails.vue';
import { useCurrentGroup } from './composables/useCurrentGroup';
import GroupQuickBlastEditForm from '~/components/quick-blast/edit/groups/GroupQuickBlastEditForm.vue';
import qs from 'qs';

const { onCorporate } = useCurrentGroup();

const paths = [
    {
        path: '/message-hub',
        component: MessageHub,
        beforeEnter: () => {
            if (!canUseMessageHub()) {
                window.location.assign('/');
                return false;
            }

            return true;
        },
        children: [
            {
                path: '',
                name: 'message-hub',
                component: MessageHubNoThreadSelected,
            },
            {
                path: 'new-message',
                name: 'message-hub.new-message',
                component: MessageHubNewConversation,
            },
            {
                path: 'bulk-actions',
                name: 'message-hub.bulk-actions',
                component: MessageHubBulkActions,
            },
            {
                path: 'scheduled',
                name: 'message-hub.scheduled-messages',
                component: MessageHubScheduledMessages,
            },
            {
                path: 'scheduled/:scheduledMessageId',
                name: 'message-hub.scheduled-message',
                component: MessageHubScheduledMessage,
            },
            {
                path: ':contactId',
                name: 'message-hub.conversation',
                component: MessageHubConversation,
                children: [
                    {
                        path: 'view',
                        name: 'message-hub.contact-view',
                        component: MessageHubContactView,
                    },
                ],
            },
            // Add 404 catch-all route
            {
                path: ':pathMatch(.*)*',
                name: 'not-found',
                component: MessageHubConversation,
            },
        ],
    },
    {
        path: '/messaging',
        children: [
            {
                path: 'quick-blast',
                children: [
                    {
                        path: 'new',
                        component: GroupQuickBlastEditForm,
                        name: 'quick-blast.new',
                        meta: { title: 'Create Quick Blast' },
                        beforeEnter: (to) => {
                            if (!to.query.duplicate_from_id || onCorporate()) {
                                window.location.assign('/messaging/quick-blast');
                                return false;
                            }

                            return true;
                        },
                    },
                    {
                        path: ':id(\\d+)',
                        component: GroupQuickBlastEditForm,
                        name: 'quick-blast.edit',
                        meta: { title: 'Campaign Details' },
                        props: (route) => ({ campaignId: Number(route.params.id) }),
                        beforeEnter: () => {
                            if (onCorporate()) {
                                return { name: 'campaigns' };
                            }
                        },
                    },
                ],
            },
            {
                path: 'campaigns',
                children: [
                    {
                        path: '',
                        name: 'campaigns',
                        component: CampaignList,
                        meta: { title: 'Campaigns' },
                    },
                    {
                        path: 'new',
                        name: 'campaigns.new',
                        component: CampaignStepForm,
                        meta: { title: 'Create Campaign' },
                        beforeEnter: (to) => {
                            if (onCorporate()) {
                                return;
                            }

                            if (to.query.duplicate_from_id) {
                                return {
                                    name: 'quick-blast.new',
                                    query: { duplicate_from_id: to.query.duplicate_from_id },
                                };
                            }

                            window.location.assign('/messaging/quick-blast');
                            return false;
                        },
                    },
                    {
                        path: ':id(\\d+)',
                        component: CampaignDetails,
                        props: (route) => ({ campaignId: Number(route.params.id) }),
                        children: [
                            {
                                path: '',
                                name: 'campaigns.edit',
                                component: CampaignStepForm,
                                meta: { title: 'Campaign Details' },
                                props: (route) => ({ campaignId: Number(route.params.id) }),
                                beforeEnter: (to) => {
                                    if (onCorporate()) {
                                        return;
                                    }

                                    return { name: 'quick-blast.edit', params: to.params, query: to.query };
                                },
                            },
                            {
                                path: 'contacts',
                                name: 'campaigns.contacts',
                                component: CampaignContacts,
                                meta: { title: 'Campaign Contacts' },
                                props: (route) => ({ campaignId: Number(route.params.id) }),
                                beforeEnter: () => {
                                    if (onCorporate()) {
                                        return;
                                    }

                                    return { name: 'campaigns' };
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    },
];

export const routes = createRouter({
    history: createWebHistory(),
    routes: paths,
    scrollBehavior() {
        return { left: 0, top: 0 };
    },
    stringifyQuery: (query) => {
        return qs.stringify(query, { arrayFormat: 'brackets', skipNulls: true });
    },
    parseQuery: (query) => {
        return qs.parse(query, { arrayFormat: 'brackets', skipNulls: true, ignoreQueryPrefix: true });
    },
});

routes.afterEach((to) => {
    if (to.meta?.title) {
        document.title = `${to.meta.title} · Voxie`;
    }
});
